import {
  useEffect,
  KeyboardEvent,
  FocusEvent,
  forwardRef,
  useRef,
  useImperativeHandle,
  ChangeEvent,
  SyntheticEvent,
} from 'react';
import cn from 'classnames';

import styles from './TextInput.module.scss';

export interface ITextInputProps {
  validateOnSubmit?: boolean;
  value?: string | number;
  className?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  autoComplete?: string;
  label?: string;
  type?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  min?: string | number;
  max?: string | number;
  invalid?: boolean;
  error?: string;
  warning?: string;
  tooltip?: string;
  disableErrorStyles?: boolean;
  clearable?: boolean;
  isPassword?: boolean;
  isPhone?: boolean;
  isFocused?: boolean;
  withWarning?: boolean;
  isActiveFillInProcessMessage?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onInput?: (e: SyntheticEvent) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onClear?: () => void;
}

export const TextInput = forwardRef(
  (
    {
      value,
      className = '',
      id,
      type = 'text',
      label,
      disabled,
      invalid,
      error,
      warning,
      tooltip,
      disableErrorStyles = false,
      clearable = false,
      isFocused = false,
      isPhone = false,
      withWarning,
      onClear,
      ...rest
    }: ITextInputProps,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current);

    const classNames = cn([
      styles.InputField,
      className,
      {
        [styles.InputFieldInvalid]: invalid || Boolean(error),
        [styles.InputFieldWarning]: Boolean(warning),
        [styles.InputFieldDisabled]: disabled,
        [styles.InputFieldDisabledErrorStyle]: disableErrorStyles,
        [styles.IsPhone]: isPhone,
      },
    ]);

    useEffect(() => {
      if (inputRef && inputRef.current) {
        isFocused && inputRef.current.focus();
      }
    }, [isFocused]);
    return (
      <div className={classNames}>
        {label && (
          <label className={styles.Label} htmlFor={id}>
            {label}
          </label>
        )}
        {isPhone && (
          <div className={styles.PhoneWrapper}>
            <span className={styles.Phone}>+7</span>
          </div>
        )}
        <input
          className={cn(styles.Input, {
            [styles.WithWarning]: withWarning,
          })}
          value={value}
          ref={inputRef}
          disabled={disabled}
          type={type}
          {...rest}
        />
        {clearable && <button className={styles.ClearButton} type="button" onClick={onClear} hidden />}
        {withWarning && <span className={styles.WarningIcon} />}
        {error && !disableErrorStyles && <div className={styles.TextInputError}>{error}</div>}
        {warning && <div className={styles.Warning}>{warning}</div>}
        {tooltip && error && (
          <div className={styles.Tooltip}>
            <div className={styles.Arrow} />
            <div className={styles.TooltipText}>{tooltip}</div>
          </div>
        )}
      </div>
    );
  },
);
